<template>
	<div class="min-h-screen bg-gray-50 font-sans">
		<!-- Header -->
		<header class="bg-white border-b border-gray-200 py-4 px-3">
			<div class="w-full flex justify-between items-center flex-wrap gap-2">
				<div class="flex items-center gap-4">
					<router-link
						to="/application"
						class="inline-flex items-center gap-2 text-gray-500 no-underline text-sm transition-colors hover:text-orange-7"
					>
						<svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
							<path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
						</svg>
						Back to API Permissions
					</router-link>
				</div>
				<button
					type="button"
					class="inline-flex items-center gap-2 bg-orange-7 text-white py-2.5 px-4 rounded-lg text-sm font-medium border-none cursor-pointer transition-colors hover:bg-orange-6"
					@click="openCreateRouteModal"
				>
					<svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
						<path stroke-linecap="round" stroke-linejoin="round" d="M12 4v16m8-8H4" />
					</svg>
					Add Route
				</button>
			</div>
		</header>

		<!-- Main Content -->
		<main class="w-full p-3">
			<!-- Application Info Card -->
			<div class="bg-white rounded-xl shadow-sm overflow-hidden mb-4">
				<div class="p-6">
					<h1 class="text-2xl font-semibold text-gray-900 m-0">Application Details</h1>
					<div v-if="loadingApp" class="p-8 text-center">
						<span class="text-gray-500">Loading...</span>
					</div>
					<div v-else-if="application" class="grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-6 mt-4">
						<div>
							<label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">ID</label>
							<span class="text-sm text-gray-900">{{ application.id }}</span>
						</div>
						<div>
							<label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Partner</label>
							<span class="text-sm font-medium text-gray-900">{{ application.partner || '-' }}</span>
						</div>
						<div>
							<label class="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">IP Address</label>
							<span class="text-sm text-gray-900 font-mono">{{ application.ipaddress || '-' }}</span>
						</div>
					</div>
				</div>
			</div>

			<!-- Routes Card -->
			<div class="bg-white rounded-xl shadow-sm overflow-hidden">
				<!-- Card Header -->
				<div class="py-4 px-6 border-b border-gray-200 flex flex-wrap justify-between items-center gap-4">
					<div class="flex items-center gap-3">
						<h2 class="text-lg font-semibold text-gray-900 m-0">Application Routes</h2>
						<span class="inline-flex items-center py-1 px-2.5 text-xs font-medium bg-orange-2 text-orange-7 rounded-full">
							{{ totalRows }} total
						</span>
					</div>
					<div class="flex items-center gap-3">
						<!-- Search -->
						<div class="relative">
							<svg class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
								<path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
							</svg>
							<input
								v-model="searchQuery"
								type="text"
								placeholder="Search routes..."
								class="py-2 px-3 pl-9 border border-gray-300 rounded-lg text-sm w-64 outline-none transition-all focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
							>
						</div>
						<!-- Refresh Button -->
						<button
							type="button"
							class="inline-flex items-center gap-2 py-2 px-4 border border-gray-300 bg-white rounded-lg text-sm font-medium text-gray-700 cursor-pointer transition-colors hover:bg-gray-50"
							@click="refreshRoutes"
						>
							<svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
								<path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
							</svg>
							Refresh
						</button>
					</div>
				</div>

				<!-- Table -->
				<div class="overflow-x-auto">
					<!-- Loading State -->
					<div v-if="loadingRoutes" class="flex justify-center items-center py-16 px-4">
						<svg class="w-10 h-10 text-orange-7 animate-spin" fill="none" viewBox="0 0 24 24">
							<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
							<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
						</svg>
						<span class="ml-3 text-gray-500 text-sm">Loading routes...</span>
					</div>

					<!-- Empty State -->
					<div v-else-if="filteredRoutes.length === 0" class="text-center py-16 px-4">
						<svg class="w-12 h-12 text-gray-300 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
							<path stroke-linecap="round" stroke-linejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
						</svg>
						<h3 class="text-base font-medium text-gray-700 m-0 mb-1">No routes</h3>
						<p class="text-sm text-gray-500 m-0">Get started by adding a new route.</p>
					</div>

					<!-- Data Table -->
					<table v-else class="w-full border-collapse">
						<thead>
							<tr class="bg-gray-50">
								<th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Route</th>
								<th class="py-3 px-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">GET</th>
								<th class="py-3 px-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">PUT</th>
								<th class="py-3 px-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">POST</th>
								<th class="py-3 px-2 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide">DELETE</th>
								<th class="py-3 px-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Auth Level</th>
								<th class="py-3 px-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wide">Actions</th>
							</tr>
						</thead>
						<tbody>
							<tr
								v-for="route in paginatedRoutes"
								:key="route.id"
								class="border-t border-gray-200 transition-colors hover:bg-gray-50"
							>
								<td class="py-3 px-4">
									<input
										v-model="route.route"
										type="text"
										class="py-1.5 px-2 border border-gray-200 rounded-md text-sm w-full min-w-48 outline-none transition-colors font-mono focus:border-orange-7"
										@blur="updateRoute(route, 'route', route.route)"
									>
								</td>
								<td class="py-3 px-2 text-center">
									<input
										type="checkbox"
										:checked="Boolean(route.method_get)"
										class="w-5 h-5 accent-orange-7 cursor-pointer"
										@change="toggleMethod(route, 'method_get', $event)"
									>
								</td>
								<td class="py-3 px-2 text-center">
									<input
										type="checkbox"
										:checked="Boolean(route.method_put)"
										class="w-5 h-5 accent-orange-7 cursor-pointer"
										@change="toggleMethod(route, 'method_put', $event)"
									>
								</td>
								<td class="py-3 px-2 text-center">
									<input
										type="checkbox"
										:checked="Boolean(route.method_post)"
										class="w-5 h-5 accent-orange-7 cursor-pointer"
										@change="toggleMethod(route, 'method_post', $event)"
									>
								</td>
								<td class="py-3 px-2 text-center">
									<input
										type="checkbox"
										:checked="Boolean(route.method_delete)"
										class="w-5 h-5 accent-orange-7 cursor-pointer"
										@change="toggleMethod(route, 'method_delete', $event)"
									>
								</td>
								<td class="py-3 px-4">
									<input
										v-model="route.authLevel"
										type="text"
										class="py-1.5 px-2 border border-gray-200 rounded-md text-sm w-full min-w-16 max-w-24 outline-none transition-colors focus:border-orange-7"
										@blur="updateRoute(route, 'authLevel', route.authLevel)"
									>
								</td>
								<td class="py-3 px-4 text-right">
									<button
										type="button"
										class="inline-flex items-center justify-center w-8 h-8 rounded-md bg-transparent border-none text-gray-500 cursor-pointer transition-colors hover:bg-red-50 hover:text-red-600"
										title="Delete"
										@click="confirmDeleteRoute(route)"
									>
										<svg class="w-4.5 h-4.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
											<path stroke-linecap="round" stroke-linejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
										</svg>
									</button>
								</td>
							</tr>
						</tbody>
					</table>
				</div>

				<!-- Pagination -->
				<div v-if="filteredRoutes.length > 0" class="py-3 px-6 border-t border-gray-200 flex flex-wrap justify-between items-center gap-4 bg-gray-50">
					<div class="flex items-center gap-2 text-sm text-gray-500">
						<span>Showing {{ startRow }} to {{ endRow }} of {{ totalRows }} results</span>
					</div>
					<div class="flex items-center gap-2">
						<select
							v-model="pageSize"
							class="py-1.5 px-3 pr-8 border border-gray-300 rounded-md text-sm bg-white cursor-pointer outline-none"
						>
							<option :value="10">10 per page</option>
							<option :value="25">25 per page</option>
							<option :value="50">50 per page</option>
							<option :value="100">100 per page</option>
						</select>
						<div class="flex items-center gap-1">
							<button
								type="button"
								:disabled="currentPage === 1"
								class="inline-flex items-center justify-center w-8 h-8 border border-gray-300 rounded-md bg-white cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
								@click="goToPage(currentPage - 1)"
							>
								<svg class="w-4 h-4 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
									<path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
								</svg>
							</button>
							<span class="px-3 text-sm text-gray-700">{{ currentPage }} / {{ totalPages }}</span>
							<button
								type="button"
								:disabled="currentPage === totalPages"
								class="inline-flex items-center justify-center w-8 h-8 border border-gray-300 rounded-md bg-white cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
								@click="goToPage(currentPage + 1)"
							>
								<svg class="w-4 h-4 text-gray-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
									<path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
								</svg>
							</button>
						</div>
					</div>
				</div>
			</div>
		</main>

		<!-- Create Route Modal -->
		<div v-if="showCreateRouteModal" class="fixed inset-0 z-50">
			<div class="fixed inset-0 bg-black/50" @click="closeCreateRouteModal"></div>
			<div class="fixed inset-0 overflow-y-auto">
				<div class="flex min-h-full items-center justify-center p-4">
					<div class="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 shadow-2xl relative z-[51]">
						<h3 class="text-xl font-semibold text-gray-900 m-0">
							Add Application Route
						</h3>
						<p class="mt-1 text-sm text-gray-500">
							Configure a new API route permission.
						</p>
						<div class="mt-6">
							<label for="route-path" class="block text-sm font-medium text-gray-700">
								Route
							</label>
							<input
								id="route-path"
								v-model="newRoute.route"
								type="text"
								placeholder="e.g., /api/users"
								class="mt-1 block w-full py-2.5 px-3.5 border border-gray-300 rounded-lg text-sm outline-none transition-all box-border font-mono focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
							>
						</div>
						<div class="mt-4">
							<label class="block text-sm font-medium text-gray-700 mb-2">
								Allowed Methods
							</label>
							<div class="flex gap-4 flex-wrap">
								<label class="flex items-center gap-2 cursor-pointer">
									<input v-model="newRoute.method_get" type="checkbox" class="w-4 h-4 accent-orange-7">
									<span class="text-sm text-gray-700">GET</span>
								</label>
								<label class="flex items-center gap-2 cursor-pointer">
									<input v-model="newRoute.method_put" type="checkbox" class="w-4 h-4 accent-orange-7">
									<span class="text-sm text-gray-700">PUT</span>
								</label>
								<label class="flex items-center gap-2 cursor-pointer">
									<input v-model="newRoute.method_post" type="checkbox" class="w-4 h-4 accent-orange-7">
									<span class="text-sm text-gray-700">POST</span>
								</label>
								<label class="flex items-center gap-2 cursor-pointer">
									<input v-model="newRoute.method_delete" type="checkbox" class="w-4 h-4 accent-orange-7">
									<span class="text-sm text-gray-700">DELETE</span>
								</label>
							</div>
						</div>
						<div class="mt-4">
							<label for="route-auth" class="block text-sm font-medium text-gray-700">
								Auth Level
							</label>
							<input
								id="route-auth"
								v-model="newRoute.authLevel"
								type="text"
								placeholder="e.g., admin, user"
								class="mt-1 block w-full py-2.5 px-3.5 border border-gray-300 rounded-lg text-sm outline-none transition-all box-border focus:border-orange-7 focus:ring-2 focus:ring-orange-2"
							>
						</div>
						<div class="mt-6 flex flex-col gap-3">
							<button
								type="button"
								:disabled="!newRoute.route.trim() || saving"
								class="w-full py-2.5 px-4 text-sm font-medium text-white bg-orange-7 border-none rounded-lg cursor-pointer transition-colors hover:bg-orange-6 disabled:opacity-50 disabled:cursor-not-allowed"
								@click="createRoute"
							>
								{{ saving ? 'Adding...' : 'Add Route' }}
							</button>
							<button
								type="button"
								class="w-full py-2.5 px-4 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg cursor-pointer transition-colors hover:bg-gray-50"
								@click="closeCreateRouteModal"
							>
								Cancel
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import Swal from 'sweetalert2';
import ApplicationService, { ApplicationModel } from 'services/ApplicationService';
import ApplicationRouteService, { ApplicationRouteModel } from 'services/ApplicationRouteService';

export default defineComponent({
	name: 'EditApiPermissionPage',
	setup() {
		const route = useRoute();
		const applicationId = computed(() => route.params.id as string);

		const application = ref<ApplicationModel | null>(null);
		const routes = ref<ApplicationRouteModel[]>([]);
		const loadingApp = ref(false);
		const loadingRoutes = ref(false);
		const saving = ref(false);
		const searchQuery = ref('');
		const currentPage = ref(1);
		const pageSize = ref(25);
		const showCreateRouteModal = ref(false);

		const newRoute = ref({
			route: '',
			method_get: false,
			method_put: false,
			method_post: false,
			method_delete: false,
			authLevel: '',
		});

		const filteredRoutes = computed(() => {
			if (!searchQuery.value.trim()) {
				return routes.value;
			}
			const query = searchQuery.value.toLowerCase();
			return routes.value.filter(r =>
				(r.route && r.route.toLowerCase().includes(query)) ||
				(r.authLevel && r.authLevel.toLowerCase().includes(query))
			);
		});

		const totalRows = computed(() => filteredRoutes.value.length);
		const totalPages = computed(() => Math.max(1, Math.ceil(totalRows.value / pageSize.value)));
		const startRow = computed(() => totalRows.value === 0 ? 0 : (currentPage.value - 1) * pageSize.value + 1);
		const endRow = computed(() => Math.min(currentPage.value * pageSize.value, totalRows.value));

		const paginatedRoutes = computed(() => {
			const start = (currentPage.value - 1) * pageSize.value;
			return filteredRoutes.value.slice(start, start + pageSize.value);
		});

		const fetchApplication = async () => {
			loadingApp.value = true;
			try {
				const allApplications = await ApplicationService.getAll();
				const found = allApplications.find(app => String(app.id) === String(applicationId.value));
				if (found) {
					application.value = found;
				} else {
					throw new Error('Application not found');
				}
			} catch (error: any) {
				await Swal.fire({
					icon: 'error',
					title: 'Error',
					text: error.message || 'Failed to load application',
					customClass: {
						popup: 'swal2-windmill',
						confirmButton: 'swal2-windmill-confirm',
					},
				});
			} finally {
				loadingApp.value = false;
			}
		};

		const fetchRoutes = async () => {
			loadingRoutes.value = true;
			try {
				routes.value = await ApplicationRouteService.getByApplicationId(applicationId.value);
			} catch (error: any) {
				await Swal.fire({
					icon: 'error',
					title: 'Error',
					text: error.message || 'Failed to load routes',
					customClass: {
						popup: 'swal2-windmill',
						confirmButton: 'swal2-windmill-confirm',
					},
				});
			} finally {
				loadingRoutes.value = false;
			}
		};

		const refreshRoutes = () => {
			currentPage.value = 1;
			fetchRoutes();
		};

		const goToPage = (page: number) => {
			if (page >= 1 && page <= totalPages.value) {
				currentPage.value = page;
			}
		};

		const openCreateRouteModal = () => {
			newRoute.value = {
				route: '',
				method_get: false,
				method_put: false,
				method_post: false,
				method_delete: false,
				authLevel: '',
			};
			showCreateRouteModal.value = true;
		};

		const closeCreateRouteModal = () => {
			showCreateRouteModal.value = false;
		};

		const createRoute = async () => {
			if (!newRoute.value.route.trim()) return;
			saving.value = true;
			try {
				await ApplicationRouteService.create({
					applicationid: Number(applicationId.value),
					route: newRoute.value.route.trim(),
					method_get: newRoute.value.method_get ? 1 : 0,
					method_put: newRoute.value.method_put ? 1 : 0,
					method_post: newRoute.value.method_post ? 1 : 0,
					method_delete: newRoute.value.method_delete ? 1 : 0,
					authLevel: newRoute.value.authLevel.trim(),
				});
				closeCreateRouteModal();
				await fetchRoutes();
				await Swal.fire({
					icon: 'success',
					title: 'Success',
					text: 'Route added successfully',
					timer: 1500,
					showConfirmButton: false,
					customClass: {
						popup: 'swal2-windmill',
					},
				});
			} catch (error: any) {
				await Swal.fire({
					icon: 'error',
					title: 'Error',
					text: error.message || 'Failed to add route',
					customClass: {
						popup: 'swal2-windmill',
						confirmButton: 'swal2-windmill-confirm',
					},
				});
			} finally {
				saving.value = false;
			}
		};

		const toggleMethod = async (routeItem: ApplicationRouteModel, field: string, event: Event) => {
			const target = event.target as HTMLInputElement;
			const newValue = target.checked ? 1 : 0;
			try {
				await ApplicationRouteService.update(routeItem.id, { [field]: newValue });
				(routeItem as any)[field] = newValue;
			} catch (error: any) {
				target.checked = !target.checked;
				await Swal.fire({
					icon: 'error',
					title: 'Error',
					text: error.message || 'Failed to update route',
					customClass: {
						popup: 'swal2-windmill',
						confirmButton: 'swal2-windmill-confirm',
					},
				});
			}
		};

		const updateRoute = async (routeItem: ApplicationRouteModel, field: string, value: string) => {
			try {
				await ApplicationRouteService.update(routeItem.id, { [field]: value });
			} catch (error: any) {
				await Swal.fire({
					icon: 'error',
					title: 'Error',
					text: error.message || 'Failed to update route',
					customClass: {
						popup: 'swal2-windmill',
						confirmButton: 'swal2-windmill-confirm',
					},
				});
				await fetchRoutes();
			}
		};

		const confirmDeleteRoute = async (routeItem: ApplicationRouteModel) => {
			const result = await Swal.fire({
				icon: 'warning',
				title: 'Delete Route',
				text: `Are you sure you want to delete the route "${routeItem.route}"?`,
				showCancelButton: true,
				confirmButtonText: 'Delete',
				cancelButtonText: 'Cancel',
				customClass: {
					popup: 'swal2-windmill',
					confirmButton: 'swal2-windmill-danger',
					cancelButton: 'swal2-windmill-cancel',
				},
			});

			if (result.isConfirmed) {
				try {
					await ApplicationRouteService.delete(routeItem.id);
					await fetchRoutes();
					await Swal.fire({
						icon: 'success',
						title: 'Deleted',
						text: 'Route deleted successfully',
						timer: 1500,
						showConfirmButton: false,
						customClass: {
							popup: 'swal2-windmill',
						},
					});
				} catch (error: any) {
					await Swal.fire({
						icon: 'error',
						title: 'Error',
						text: error.message || 'Failed to delete route',
						customClass: {
							popup: 'swal2-windmill',
							confirmButton: 'swal2-windmill-confirm',
						},
					});
				}
			}
		};

		onMounted(() => {
			fetchApplication();
			fetchRoutes();
		});

		return {
			application,
			routes,
			loadingApp,
			loadingRoutes,
			saving,
			searchQuery,
			currentPage,
			pageSize,
			showCreateRouteModal,
			newRoute,
			filteredRoutes,
			paginatedRoutes,
			totalRows,
			totalPages,
			startRow,
			endRow,
			refreshRoutes,
			goToPage,
			openCreateRouteModal,
			closeCreateRouteModal,
			createRoute,
			toggleMethod,
			updateRoute,
			confirmDeleteRoute,
		};
	},
});
</script>

<style>
@keyframes spin {
	from { transform: rotate(0deg); }
	to { transform: rotate(360deg); }
}
</style>
